import { useCallback, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { useParams } from 'react-router-dom'

import useToggle from 'hooks/useToggle'
import { useTourDealDetails } from 'hooks/TourV2/useTourDealDetails'

import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'

import { mediaQueryUp } from 'components/utils/breakpoint'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'

import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'

import Pane from 'components/Common/Pane'
import TourHeadData from '../TourHeadData'
import TourDealHero from './TourDealHero'
import TourDealSkeleton from './TourDealSkeleton'
import OfferListOfferTile from 'components/OfferList/OfferListTiles/OfferListOfferTile'
import { useTourDealFacets } from 'hooks/TourV2/useTourDealFacets'

import TextButton from 'LuxKit/Button/TextButton'
import LineFilterAltIcon from 'LuxKit/Icons/line/LineFilterAltIcon'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import TourPriceToggle from 'components/Tours/TourPriceToggle'
import Group from 'components/utils/Group'
import TourDealFilterContent from './TourDealFilterContent'
import TourDealSortHeader from './TourDealSortHeader'
import TourDealPageFilters from './TourDealPageFilters'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import debounce from 'lodash.debounce'

const PageContent = styled.div`
  padding: ${rem(20)} 0;

  ${mediaQueryUp.tablet} {
    display: grid;
    padding: ${rem(16)} 0;
    gap: ${rem(32)};
    grid-template-columns: ${rem(250)} 1fr;
    &.no-filters {
      grid-template-columns: 1fr;
    }
  }
`

const OfferListWrapper = styled(LayoutContainer)`
  padding-inline: 0;

  ${mediaQueryUp.tablet} {
    padding-inline: ${rem(16)};
  }

  ${mediaQueryUp.desktop} {
    && {
      max-width: ${rem(1352)};
    }
  }
`

const ListWrapper = styled.div`
  > * + * {
    margin-top: ${rem(8)};
  }
`

const HeaderPane = styled(Pane)`
  box-shadow: ${props => props.theme.shadow.flat.small};
  padding: ${rem(12)} 0;
`

const StyledLayoutContainer = styled(LayoutContainer)`
  padding-inline: 0;

  ${mediaQueryUp.tablet} {
    padding-inline: ${rem(16)};
  }

  ${mediaQueryUp.desktop} {
    && {
      max-width: ${rem(1352)};
    }
  }
`

const DEFAULT_SORT_PARAMS: App.Tours.TourDealSortState = {
  sortBy: 'price',
  sortOrder: 'asc',
}

function TourDealPage() {
  const intl = useIntl()
  const { slug = '' } = useParams<'slug'>()
  const [filters, setFilters] = useState<App.Tours.TourDealFiltersApplied>({})
  const [sortParams, setSortParams] = useState<App.Tours.TourDealSortState>(DEFAULT_SORT_PARAMS)
  const [tourDeal, isFetchingTourDeal] = useTourDealDetails(slug, filters, sortParams)
  const [initialFacets, isFetchingInitialFacets] = useTourDealFacets(slug)
  const isFetching = isFetchingTourDeal || isFetchingInitialFacets

  const isMobileScreen = useScreenSizeOnly('mobile')

  const {
    on: openFilters,
    off: closeFilters,
    value: showFilters,
  } = useToggle()

  const onFilterChange = useMemo(() => debounce((filtersApplied: App.Tours.TourDealFiltersApplied) => {
    setFilters({ ...filtersApplied })
  }, 250), [setFilters])

  const onSortChange = useMemo(() => debounce((value: string) => {
    const [sortBy, sortOrder] = value.split('.')
    setSortParams({ sortBy, sortOrder })
  }, 250), [setSortParams])

  const onFilterSubmit = useCallback((filters: App.Tours.TourDealFiltersApplied, sortParams?: string) => {
    onFilterChange({ ...filters })
    onSortChange(sortParams ?? `${DEFAULT_SORT_PARAMS.sortBy}.${DEFAULT_SORT_PARAMS.sortOrder}`)
    closeFilters()
  }, [onFilterChange, onSortChange, closeFilters])

  const shouldShowFilters = useMemo(() => !!tourDeal && tourDeal.tours.length > 0, [tourDeal])

  return <>
    {!!tourDeal && initialFacets && <AnalyticsPageContext.Provider value={AnalyticsPage.search}>
      <div>
        <TourHeadData />
        <TourDealHero tourDeal={tourDeal} />
        <CSSBreakpoint max="mobile">
          {shouldShowFilters && <HeaderPane>
            <StyledLayoutContainer>
              <Group direction="horizontal" verticalAlign="center" gap={8}>
                <TextButton
                  kind="secondary"
                  variant="default"
                  startIcon={<LineFilterAltIcon />}
                  onClick={openFilters}
                  size="medium"
                >
                  {intl.formatMessage(
                    {
                      id: 'qQDH1lDy_cJawos9c5cM',
                      defaultMessage: 'Sort & filter',
                      description: 'Button label to open the sort and filter panel for tour deals',
                    },
                  )}
                </TextButton>
                <TourPriceToggle />
              </Group>
            </StyledLayoutContainer>
          </HeaderPane>}
        </CSSBreakpoint>
        <OfferListWrapper>
          <PageContent>
            <CSSBreakpoint max="mobile">
              { /* The check for isMobileScreen is to ensure that the filter modal closes when if the screen is resized */ }
              {shouldShowFilters && isMobileScreen && <TourDealPageFilters
                open={showFilters}
                onClose={closeFilters}
                searchFacets={tourDeal.facets ?? initialFacets}
                onFilterSubmit={onFilterSubmit}
                initialFacets={initialFacets}
                filtersApplied={filters}
                sortOrderApplied={`${sortParams.sortBy}.${sortParams.sortOrder}`}
              />}
            </CSSBreakpoint>
            <CSSBreakpoint min="tablet">
              <TourDealFilterContent
                searchFacets={shouldShowFilters ? tourDeal.facets ?? initialFacets : undefined}
                initialFacets={initialFacets}
                onFilterChange={onFilterChange}
                filtersApplied={filters}
              />
            </CSSBreakpoint>
            <VerticalSpacer gap={16}>
              <CSSBreakpoint min="tablet">
                {shouldShowFilters && <div>
                  <TourDealSortHeader
                    totalNumberOfOffers={tourDeal.tours.length}
                    selectedOption={`${sortParams.sortBy}.${sortParams.sortOrder}`}
                    onChange={onSortChange}
                  />
                </div>}
              </CSSBreakpoint>
              <VerticalSpacer gap={16}>
                {!shouldShowFilters && (
                  <Pane padding={16}>
                    <Group direction="vertical" gap={16} horizontalAlign="center">
                      <Heading variant="heading3" colour="light-foreground-alt">
                        {intl.formatMessage(
                          {
                            id: 'RjwLV7qM7G9ySrM7GacQ',
                            defaultMessage: 'No deals available',
                            description: 'Heading shown when no tour deals are found in the selected region',
                          },
                        )}
                      </Heading>
                      <BodyText variant="medium" colour="light-foreground-alt">
                        {intl.formatMessage(
                          {
                            id: 'eTISKWs4Oi1XtfwTGDWM',
                            defaultMessage: 'Sorry, we couldn’t find any tours or cruises in this region',
                            description: 'Message shown when no tour deals match the current region filter',
                          },
                        )}
                      </BodyText>
                    </Group>
                  </Pane>
                )}
                {tourDeal.tours.map((tour) => (
                  <ListWrapper key={tour.id} id="tour-deal-offer-list">
                    <OfferListOfferTile
                      offerId={`tour-${tour.id}`}
                      position={0}
                      wrapperClass="offer-tile"
                      tileStyle="search"
                    />
                  </ListWrapper>
                ))}
              </VerticalSpacer>
            </VerticalSpacer>
          </PageContent>
        </OfferListWrapper>
      </div>
    </AnalyticsPageContext.Provider>}

    {isFetching && <TourDealSkeleton />}
  </>
}

export default TourDealPage
